import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import { 
  Sparkles, 
  Fingerprint,
  Target, 
  Layers, 
  Grid3X3, 
  Award, 
  Shield,
  Rocket, 
  CheckCircle2,
  GitBranch,
  ChevronDown,
  Swords,
  Users,
  Compass,
  BarChart3
} from "lucide-react";
import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";


type ViewMode = "executive" | "full";

interface ReportNavigationProps {
  activeSection?: string;
  onNavigate: (sectionId: string) => void;
  viewMode?: ViewMode;
}

const EXECUTIVE_SECTIONS = new Set([
  "executive-summary",
  "dominant-strategy",
  "commitment",
  "action-plan",
  "go-decision",
  "lifecycle",
]);

// Grouped sections matching actual Report.tsx render order
const sectionGroups = [
  {
    label: "Überblick",
    sections: [
      { id: "executive-summary", label: "Summary", icon: Sparkles },
      { id: "decision-snapshot", label: "Snapshot", icon: Fingerprint },
    ],
  },
  {
    label: "Kontext",
    sections: [
      { id: "strategic-framework", label: "Rahmen", icon: Compass },
      { id: "success-criteria", label: "Erfolg", icon: Target },
      { id: "conflict-section", label: "Konflikt", icon: Swords },
      { id: "team-perspectives", label: "Team", icon: Users },
    ],
  },
  {
    label: "Analyse",
    sections: [
      { id: "options", label: "Optionen", icon: Layers },
      { id: "matrix", label: "Matrix", icon: Grid3X3 },
      { id: "dominant-strategy", label: "Strategie", icon: Award },
    ],
  },
  {
    label: "Entscheidung",
    sections: [
      { id: "strategic-path", label: "Pfad", icon: GitBranch },
      { id: "commitment", label: "Commit", icon: Shield },
      { id: "action-plan", label: "Plan", icon: Rocket },
      { id: "go-decision", label: "Go/Pivot", icon: CheckCircle2 },
    ],
  },
  {
    label: "Lifecycle",
    sections: [
      { id: "lifecycle", label: "Lifecycle", icon: BarChart3 },
    ],
  },
];

// Flat list for lookups
const allSections = sectionGroups.flatMap(g => g.sections);

export function ReportNavigation({ activeSection, onNavigate, viewMode = "full" }: ReportNavigationProps) {
  // Track which sections actually exist in DOM
  const [presentSections, setPresentSections] = useState<Set<string>>(new Set());

  useEffect(() => {
    const checkPresence = () => {
      const present = new Set<string>();
      allSections.forEach(s => {
        if (document.getElementById(s.id)) present.add(s.id);
      });
      setPresentSections(present);
    };
    // Check initially and on mutations
    checkPresence();
    const observer = new MutationObserver(checkPresence);
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  const activeItem = allSections.find(s => s.id === activeSection) || allSections[0];
  const ActiveIcon = activeItem.icon;

  // Progress: only count present sections
  const presentList = allSections.filter(s => presentSections.has(s.id));
  const activeIndex = presentList.findIndex(s => s.id === activeSection);
  const progress = presentList.length > 1 
    ? Math.round(((activeIndex >= 0 ? activeIndex : 0) / (presentList.length - 1)) * 100) 
    : 0;

  // Find which group the active section belongs to
  const activeGroupIndex = sectionGroups.findIndex(g => 
    g.sections.some(s => s.id === activeSection)
  );

  return (
    <>
      {/* Desktop Navigation - Fixed Sidebar */}
      <nav className="fixed left-4 top-20 bottom-4 z-40 hidden xl:flex flex-col bg-background/90 backdrop-blur-md rounded-xl border border-border/50 shadow-lg w-[168px] overflow-hidden max-h-[calc(100vh-6rem)]">
        {/* Progress bar */}
        <div className="h-1 w-full bg-muted/50">
          <div 
            className="h-full bg-primary transition-all duration-500 ease-out rounded-full"
            style={{ width: `${progress}%` }}
          />
        </div>

        <div className="p-2 space-y-1 overflow-y-auto flex-1 min-h-0">
          {sectionGroups.map((group, groupIdx) => {
            // Only show group if at least one section is present
            const visibleSections = group.sections.filter(s => 
              presentSections.has(s.id) && (viewMode === "full" || EXECUTIVE_SECTIONS.has(s.id))
            );
            if (visibleSections.length === 0) return null;

            return (
              <div key={group.label}>
                {/* Group label */}
                <p className={cn(
                  "text-[10px] font-semibold uppercase tracking-wider px-2 pt-2 pb-1",
                  groupIdx === activeGroupIndex 
                    ? "text-primary" 
                    : "text-muted-foreground/60"
                )}>
                  {group.label}
                </p>

                {visibleSections.map((section) => {
                  const Icon = section.icon;
                  const isActive = activeSection === section.id;
                  
                  return (
                    <button
                      key={section.id}
                      onClick={() => onNavigate(section.id)}
                      className={cn(
                        "flex items-center gap-2 w-full px-2.5 py-1.5 rounded-md text-xs font-medium transition-all duration-200",
                        "hover:bg-accent/80 hover:text-accent-foreground",
                        isActive 
                          ? "bg-primary/10 text-primary shadow-sm" 
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <Icon className={cn(
                        "h-3.5 w-3.5 shrink-0 transition-colors",
                        isActive ? "text-primary" : "text-muted-foreground/70"
                      )} />
                      <span className="truncate">{section.label}</span>
                      {isActive && (
                        <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary shrink-0" />
                      )}
                    </button>
                  );
                })}

                {/* Divider between groups (except last) */}
                {groupIdx < sectionGroups.length - 1 && (
                  <div className="mx-2 my-1 border-t border-border/30" />
                )}
              </div>
            );
          })}
        </div>

        {/* Footer with section counter */}
        <div className="px-3 py-2 border-t border-border/30 bg-muted/20">
          <p className="text-[10px] text-muted-foreground text-center">
            {(activeIndex >= 0 ? activeIndex : 0) + 1} / {presentList.length}
          </p>
        </div>
      </nav>

      {/* Mobile Navigation - Sticky Dropdown */}
      <div className="sticky top-[73px] z-30 xl:hidden bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border-b">
        {/* Progress bar mobile */}
        <div className="h-0.5 w-full bg-muted/50">
          <div 
            className="h-full bg-primary transition-all duration-500 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>
        <div className="px-4 py-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="outline" 
                className="w-full justify-between gap-2 h-10"
              >
                <div className="flex items-center gap-2">
                  <ActiveIcon className="h-4 w-4 text-primary" />
                  <span className="font-medium">{activeItem.label}</span>
                  <span className="text-[10px] text-muted-foreground">
                    {(activeIndex >= 0 ? activeIndex : 0) + 1}/{presentList.length}
                  </span>
                </div>
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="start" 
              className="w-[calc(100vw-2rem)] max-w-md bg-background border shadow-lg"
            >
              {sectionGroups.map((group, groupIdx) => {
                const visibleSections = group.sections.filter(s => 
                  presentSections.has(s.id) && (viewMode === "full" || EXECUTIVE_SECTIONS.has(s.id))
                );
                if (visibleSections.length === 0) return null;
                return (
                  <div key={group.label}>
                    <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-muted-foreground/70 py-1">
                      {group.label}
                    </DropdownMenuLabel>
                    {visibleSections.map((section) => {
                      const Icon = section.icon;
                      const isActive = activeSection === section.id;
                      
                      return (
                        <DropdownMenuItem
                          key={section.id}
                          onClick={() => onNavigate(section.id)}
                          className={cn(
                            "flex items-center gap-3 py-2.5 cursor-pointer",
                            isActive && "bg-primary/10 text-primary"
                          )}
                        >
                          <Icon className={cn("h-4 w-4", isActive ? "text-primary" : "text-muted-foreground")} />
                          <span className={cn("font-medium", isActive && "text-primary")}>{section.label}</span>
                          {isActive && (
                            <span className="ml-auto h-1.5 w-1.5 rounded-full bg-primary" />
                          )}
                        </DropdownMenuItem>
                      );
                    })}
                    {groupIdx < sectionGroups.length - 1 && <DropdownMenuSeparator />}
                  </div>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </>
  );
}

// Export for scroll detection sync
export const REPORT_SECTION_IDS = allSections.map(s => s.id);
